import { Check, X, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { formatDate, formatTime } from '@/lib/format';
import { PlayerAvatar } from '@/components/shared/player-avatar';

interface MakeupSession {
  date: string;
  startTime: string;
}

export interface MakeupItem {
  id: string;
  playerName: string;
  playerFirstName: string;
  playerLastName: string;
  status: string;
  originalSession: { date: string; startTime: string } | null;
  makeupSession?: MakeupSession | null;
}

interface SharedMakeupsTableProps {
  title: string;
  description: string;
  makeups: MakeupItem[];
  variant: 'pending' | 'scheduled' | 'history';
  maxHistoryItems?: number;
  onSchedule?: (makeupId: string) => void;
  onComplete?: (makeupId: string) => void;
  onWaive?: (makeupId: string) => void;
}

export function SharedMakeupsTable({
  title,
  description,
  makeups,
  variant,
  maxHistoryItems,
  onSchedule,
  onComplete,
  onWaive,
}: SharedMakeupsTableProps) {
  if (makeups.length === 0) return null;

  const displayMakeups = variant === 'history' && maxHistoryItems
    ? makeups.slice(0, maxHistoryItems)
    : makeups;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className={variant === 'history' ? 'space-y-2' : 'space-y-3'}>
        {displayMakeups.map(makeup => (
          <div
            key={makeup.id}
            className={`flex items-center justify-between p-3 border rounded-lg ${
              variant === 'history' ? '' : 'bg-muted/40 border-border'
            }`}
          >
            <div className="flex items-center gap-3">
              <PlayerAvatar
                firstName={makeup.playerFirstName}
                lastName={makeup.playerLastName}
                variant={variant === 'history' ? 'muted' : 'coach'}
                size={variant === 'history' ? 'xs' : 'md'}
              />
              <div>
                <p className={`${variant === 'history' ? 'text-sm' : 'font-medium'} text-foreground`}>
                  {makeup.playerName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {variant === 'history' ? '' : 'Missed: '}
                  {makeup.originalSession ? formatDate(makeup.originalSession.date) : 'Unknown'}
                  {variant === 'pending' && makeup.originalSession && ` ${formatTime(makeup.originalSession.startTime)}`}
                </p>
                {variant === 'scheduled' && makeup.makeupSession && (
                  <p className="text-xs text-primary font-medium">
                    Makeup: {formatDate(makeup.makeupSession.date)} {formatTime(makeup.makeupSession.startTime)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {variant === 'pending' && onSchedule && (
                <Button size="sm" onClick={() => onSchedule(makeup.id)}>
                  <Calendar className="w-4 h-4 mr-1" />
                  Schedule
                </Button>
              )}
              {variant === 'scheduled' && onComplete && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                  onClick={() => onComplete(makeup.id)}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Done
                </Button>
              )}
              {(variant === 'pending' || variant === 'scheduled') && onWaive && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-red-500"
                  onClick={() => onWaive(makeup.id)}
                  title="Waive makeup"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
              {variant === 'history' && (
                <Badge variant={makeup.status === 'completed' ? 'default' : 'secondary'}>
                  {makeup.status}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
